import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@data/categories';
import localProducts from '@data/products';
import ProductCard from '@components/product/ProductCard';

const API = import.meta.env.VITE_API_URL || '/api';

// ── Fade-in observer (re-runs when deps change so dynamically loaded elements get observed) ──
function useFadeIn(deps = []) {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('vis'); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.fi').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

const DEITIES = [
  { name: 'Lord Ganesha', sub: 'Vighnaharta', emoji: '🐘', desc: 'Remover of obstacles, bestower of wisdom' },
  { name: 'Lord Shiva', sub: 'Mahadeva', emoji: '🔱', desc: 'The supreme destroyer and transformer' },
  { name: 'Goddess Lakshmi', sub: 'Devi', emoji: '🪷', desc: 'Goddess of wealth, prosperity and fortune' },
  { name: 'Lord Vishnu', sub: 'Preserver', emoji: '🌸', desc: 'The preserver and protector of the universe' },
  { name: 'Goddess Durga', sub: 'Shakti', emoji: '⚔️', desc: 'The divine mother, destroyer of evil' },
  { name: 'Lord Krishna', sub: 'Govinda', emoji: '🪈', desc: 'The divine cowherd, teacher of the Gita' },
];

const MANTRAS = [
  { text: 'ॐ नमः शिवाय', trans: 'Om Namah Shivaya', meaning: 'I bow to Lord Shiva, the auspicious one' },
  { text: 'हरे कृष्ण हरे कृष्ण', trans: 'Hare Krishna', meaning: 'Oh Lord Krishna, please engage me in Your service' },
  { text: 'ॐ गं गणपतये नमः', trans: 'Om Gam Ganapataye Namah', meaning: 'I bow to Lord Ganesha, the remover of obstacles' },
];

const BLOG_POSTS = [
  { title: 'The Sacred Science of Rudraksha', category: 'Spirituality', emoji: '📿', excerpt: 'Discover the divine power and ancient wisdom behind Rudraksha beads and their transformative effects on consciousness.', date: 'Dec 15, 2024' },
  { title: 'How to Perform Daily Puja at Home', category: 'Puja Guide', emoji: '🪔', excerpt: 'A complete guide to setting up your home temple and performing daily worship with authentic Vedic rituals.', date: 'Dec 10, 2024' },
  { title: 'Understanding the 108 Names of Lord Vishnu', category: 'Vedic Wisdom', emoji: '📖', excerpt: 'Explore the profound significance of Ashtottara Shatanamavali and the cosmic attributes of the preserver.', date: 'Dec 5, 2024' },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  useFadeIn([loadingProducts]);

  useEffect(() => {
    fetch(`${API}/get-products.php?limit=8&sort=newest`)
      .then(r => r.json())
      .then(d => {
        const items = d.data || d.products || [];
        setFeatured(items.length > 0 ? items : localProducts.slice(0, 8));
      })
      .catch(() => setFeatured(localProducts.slice(0, 8)))
      .finally(() => setLoadingProducts(false));
  }, []);

  return (
    <div>
      {/* ══ HERO ══ */}
      <section style={{position:'relative',minHeight:'88vh',display:'flex',alignItems:'center',justifyContent:'center',textAlign:'center',overflow:'hidden',padding:'4rem 1.5rem'}}>
        {/* Gradient bg */}
        <div style={{position:'absolute',inset:0,background:'radial-gradient(ellipse 80% 60% at 50% 40%,rgba(255,107,0,.18) 0%,rgba(36,0,70,.3) 50%,transparent 100%)',pointerEvents:'none'}} />
        {/* Mandala SVG */}
        <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',width:'min(70vw,600px)',height:'min(70vw,600px)',opacity:.07,animation:'spin 90s linear infinite',pointerEvents:'none'}}>
          <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="100" cy="100" r="90" stroke="#FFB800" strokeWidth=".5"/>
            <circle cx="100" cy="100" r="75" stroke="#FF6B00" strokeWidth=".4"/>
            <circle cx="100" cy="100" r="60" stroke="#FFB800" strokeWidth=".5"/>
            <circle cx="100" cy="100" r="45" stroke="#FF6B00" strokeWidth=".4"/>
            <circle cx="100" cy="100" r="30" stroke="#FFB800" strokeWidth=".5"/>
            {Array.from({length:12},(_,i)=>{
              const a=i*30*Math.PI/180;
              return <line key={i} x1="100" y1="100" x2={100+90*Math.cos(a)} y2={100+90*Math.sin(a)} stroke="#FFB800" strokeWidth=".3" />;
            })}
            {Array.from({length:8},(_,i)=>{
              const a=i*45*Math.PI/180;
              return <polygon key={i} points={`100,${100-40} ${100+10*Math.cos(a+1.5)},${100+10*Math.sin(a+1.5)} 100,${100+40} ${100+10*Math.cos(a-.2)},${100+10*Math.sin(a-.2)}`} stroke="#FF6B00" strokeWidth=".3" fill="none" />;
            })}
            <text x="100" y="108" textAnchor="middle" fill="#FFB800" fontSize="24" fontFamily="serif">ॐ</text>
          </svg>
        </div>

        <div style={{position:'relative',zIndex:1,maxWidth:780}}>
          <div className="fi" style={{fontFamily:"'Cinzel',serif",fontSize:'.7rem',letterSpacing:'.35em',textTransform:'uppercase',color:'#FF6B00',marginBottom:'1.5rem'}}>
            ✦ Sanatana Dharma ✦
          </div>
          <h1 className="fi" style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'clamp(2.2rem,5vw,4rem)',fontWeight:900,lineHeight:1.1,marginBottom:'1.5rem',background:'linear-gradient(135deg,#FFE082 0%,#FFB800 50%,#FF6B00 100%)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
            Welcome to<br/>CosmicHindu
          </h1>
          <div className="fi" style={{fontFamily:"'Cinzel',serif",fontSize:'clamp(1rem,2.5vw,1.5rem)',color:'rgba(255,224,130,.75)',marginBottom:'.8rem',letterSpacing:'.05em'}}>
            Sacred Products for Spiritual Seekers
          </div>
          <p className="fi" style={{fontFamily:"'Crimson Pro',serif",fontSize:'1.1rem',color:'rgba(255,224,130,.55)',lineHeight:1.8,maxWidth:580,margin:'0 auto 2.5rem',fontStyle:'italic'}}>
            Discover authentic divine products crafted with devotion — from blessed Rudraksha to sacred idols, astro stones to puja essentials. Your spiritual journey begins here.
          </p>
          <div className="fi" style={{display:'flex',gap:'1rem',justifyContent:'center',flexWrap:'wrap'}}>
            <Link to="/shop" style={{display:'inline-block',padding:'.9rem 2.2rem',fontFamily:"'Cinzel',serif",fontSize:'.8rem',fontWeight:700,letterSpacing:'.15em',textTransform:'uppercase',background:'linear-gradient(135deg,#FF6B00,#FFB800)',color:'#0D0500',borderRadius:4,textDecoration:'none',transition:'transform .2s'}}>
              🛍 Explore Sacred Shop
            </Link>
            <Link to="/about" style={{display:'inline-block',padding:'.9rem 2.2rem',fontFamily:"'Cinzel',serif",fontSize:'.8rem',fontWeight:600,letterSpacing:'.15em',textTransform:'uppercase',border:'1px solid rgba(255,184,0,.4)',color:'rgba(255,224,130,.8)',borderRadius:4,textDecoration:'none',transition:'all .2s'}}>
              Our Story →
            </Link>
          </div>
        </div>
      </section>

      {/* ══ TRUST BAR ══ */}
      <div style={{background:'linear-gradient(90deg,rgba(255,107,0,.12),rgba(255,184,0,.08),rgba(255,107,0,.12))',borderTop:'1px solid rgba(255,184,0,.18)',borderBottom:'1px solid rgba(255,184,0,.18)',padding:'1.1rem 1.5rem'}}>
        <div style={{maxWidth:1200,margin:'0 auto',display:'flex',justifyContent:'center',flexWrap:'wrap',gap:'1.5rem 3rem'}}>
          {[
            {icon:'🚚',t:'Free Shipping',s:'On orders ₹999+'},
            {icon:'✅',t:'100% Authentic',s:'Certified products'},
            {icon:'🔒',t:'Secure Payments',s:'UPI · GPay · Cards'},
            {icon:'🔄',t:'Easy Returns',s:'7-day policy'},
            {icon:'🙏',t:'Blessed & Sacred',s:'Puja-consecrated items'},
          ].map(f=>(
            <div key={f.t} style={{display:'flex',alignItems:'center',gap:'.7rem'}}>
              <span style={{fontSize:'1.3rem'}}>{f.icon}</span>
              <div>
                <div style={{fontFamily:"'Cinzel',serif",fontSize:'.7rem',fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#FFB800'}}>{f.t}</div>
                <div style={{fontSize:'.7rem',color:'rgba(255,224,130,.45)',fontFamily:"'Crimson Pro',serif"}}>{f.s}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ══ CATEGORIES ══ */}
      <section style={{padding:'5rem 1.5rem'}}>
        <div style={{maxWidth:1200,margin:'0 auto'}}>
          <div className="fi" style={{textAlign:'center',marginBottom:'3rem'}}>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:'.65rem',letterSpacing:'.35em',textTransform:'uppercase',color:'#FF6B00',marginBottom:'.6rem'}}>✦ Browse By Category ✦</div>
            <h2 style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'clamp(1.6rem,3.5vw,2.5rem)',background:'linear-gradient(135deg,#FFE082,#FFB800)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',marginBottom:'.8rem'}}>Sacred Collections</h2>
            <div style={{width:60,height:2,background:'linear-gradient(90deg,transparent,#FF6B00,transparent)',margin:'0 auto'}} />
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:'1.2rem'}}>
            {categories.slice(0,6).map((cat,i)=>(
              <Link key={cat.id} to={`/shop/${cat.id}`} className="fi" style={{textDecoration:'none',animationDelay:`${i*.08}s`}}>
                <div style={{background:'linear-gradient(135deg,rgba(255,107,0,.08),rgba(36,0,70,.3))',border:'1px solid rgba(255,184,0,.18)',borderRadius:8,padding:'1.8rem 1rem',textAlign:'center',transition:'all .3s',cursor:'pointer'}}>
                  <div style={{fontSize:'2.5rem',marginBottom:'.8rem'}}>{cat.icon}</div>
                  <div style={{fontFamily:"'Cinzel',serif",fontSize:'.78rem',fontWeight:600,letterSpacing:'.08em',color:'#FFE082',marginBottom:'.4rem'}}>{cat.name}</div>
                  <div style={{fontSize:'.72rem',color:'rgba(255,224,130,.45)',fontFamily:"'Crimson Pro',serif",fontStyle:'italic'}}>{cat.description}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FEATURED PRODUCTS ══ */}
      <section style={{padding:'4rem 1.5rem 5rem',background:'rgba(255,184,0,.03)'}}>
        <div style={{maxWidth:1300,margin:'0 auto'}}>
          <div className="fi" style={{textAlign:'center',marginBottom:'3rem'}}>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:'.65rem',letterSpacing:'.35em',textTransform:'uppercase',color:'#FF6B00',marginBottom:'.6rem'}}>✦ Handpicked For You ✦</div>
            <h2 style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'clamp(1.6rem,3.5vw,2.5rem)',background:'linear-gradient(135deg,#FFE082,#FFB800)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',marginBottom:'.8rem'}}>Featured Products</h2>
            <div style={{width:60,height:2,background:'linear-gradient(90deg,transparent,#FF6B00,transparent)',margin:'0 auto'}} />
          </div>
          {loadingProducts ? (
            <div style={{textAlign:'center',padding:'3rem',fontFamily:"'Cinzel',serif",fontSize:'.8rem',letterSpacing:'.15em',color:'rgba(255,184,0,.4)'}}>
              🌀 Loading sacred products…
            </div>
          ) : featured.length === 0 ? (
            <div style={{textAlign:'center',padding:'3rem',fontFamily:"'Cinzel',serif",fontSize:'.82rem',color:'rgba(255,224,130,.3)',letterSpacing:'.1em'}}>
              No products yet. Add products from the admin panel.
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:'1.5rem'}}>
              {featured.map((p,i)=>(
                <div key={p.id} className="fi" style={{animationDelay:`${i*.06}s`}}>
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          )}
          <div className="fi" style={{textAlign:'center',marginTop:'2.5rem'}}>
            <Link to="/shop" style={{display:'inline-block',padding:'.85rem 2.5rem',fontFamily:"'Cinzel',serif",fontSize:'.78rem',fontWeight:600,letterSpacing:'.15em',textTransform:'uppercase',border:'1px solid rgba(255,184,0,.4)',color:'rgba(255,224,130,.8)',borderRadius:4,textDecoration:'none'}}>
              View All Products →
            </Link>
          </div>
        </div>
      </section>

      {/* ══ MANTRA BLOCK ══ */}
      <section style={{padding:'5rem 1.5rem',background:'linear-gradient(135deg,rgba(255,107,0,.1) 0%,rgba(36,0,70,.4) 50%,rgba(255,107,0,.08) 100%)',borderTop:'1px solid rgba(255,184,0,.15)',borderBottom:'1px solid rgba(255,184,0,.15)',textAlign:'center'}}>
        <div style={{maxWidth:900,margin:'0 auto'}}>
          <div className="fi" style={{fontFamily:"'Cinzel',serif",fontSize:'.65rem',letterSpacing:'.35em',textTransform:'uppercase',color:'#FF6B00',marginBottom:'1.5rem'}}>✦ Daily Mantras ✦</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))',gap:'1.5rem'}}>
            {MANTRAS.map((m,i)=>(
              <div key={i} className="fi" style={{background:'rgba(255,184,0,.06)',border:'1px solid rgba(255,184,0,.18)',borderRadius:8,padding:'2rem 1.5rem',animationDelay:`${i*.1}s`}}>
                <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'1.4rem',color:'#FFB800',marginBottom:'.6rem',lineHeight:1.3}}>{m.text}</div>
                <div style={{fontFamily:"'Cinzel',serif",fontSize:'.75rem',letterSpacing:'.1em',color:'#FF6B00',marginBottom:'.5rem'}}>{m.trans}</div>
                <div style={{fontSize:'.8rem',color:'rgba(255,224,130,.5)',fontStyle:'italic',fontFamily:"'Crimson Pro',serif"}}>{m.meaning}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ DEITIES ══ */}
      <section style={{padding:'5rem 1.5rem'}}>
        <div style={{maxWidth:1200,margin:'0 auto'}}>
          <div className="fi" style={{textAlign:'center',marginBottom:'3rem'}}>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:'.65rem',letterSpacing:'.35em',textTransform:'uppercase',color:'#FF6B00',marginBottom:'.6rem'}}>✦ Divine Pantheon ✦</div>
            <h2 style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'clamp(1.6rem,3.5vw,2.5rem)',background:'linear-gradient(135deg,#FFE082,#FFB800)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',marginBottom:'.8rem'}}>Explore by Deity</h2>
            <div style={{width:60,height:2,background:'linear-gradient(90deg,transparent,#FF6B00,transparent)',margin:'0 auto'}} />
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:'1.2rem'}}>
            {DEITIES.map((d,i)=>(
              <Link key={d.name} to={`/shop?deity=${encodeURIComponent(d.name)}`} className="fi" style={{textDecoration:'none',animationDelay:`${i*.07}s`}}>
                <div style={{background:'linear-gradient(135deg,rgba(255,107,0,.06),rgba(36,0,70,.25))',border:'1px solid rgba(255,184,0,.15)',borderRadius:10,padding:'1.8rem 1rem',textAlign:'center',transition:'all .3s'}}>
                  <div style={{fontSize:'2.8rem',marginBottom:'.8rem'}}>{d.emoji}</div>
                  <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'.72rem',fontWeight:700,color:'#FFE082',marginBottom:'.3rem'}}>{d.name}</div>
                  <div style={{fontFamily:"'Cinzel',serif",fontSize:'.6rem',letterSpacing:'.12em',textTransform:'uppercase',color:'#FF6B00',marginBottom:'.5rem'}}>{d.sub}</div>
                  <div style={{fontSize:'.72rem',color:'rgba(255,224,130,.42)',fontStyle:'italic',fontFamily:"'Crimson Pro',serif",lineHeight:1.5}}>{d.desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHY US ══ */}
      <section style={{padding:'5rem 1.5rem',background:'rgba(36,0,70,.15)',borderTop:'1px solid rgba(255,184,0,.1)',borderBottom:'1px solid rgba(255,184,0,.1)'}}>
        <div style={{maxWidth:1100,margin:'0 auto'}}>
          <div className="fi" style={{textAlign:'center',marginBottom:'3rem'}}>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:'.65rem',letterSpacing:'.35em',textTransform:'uppercase',color:'#FF6B00',marginBottom:'.6rem'}}>✦ Why Choose Us ✦</div>
            <h2 style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'clamp(1.6rem,3.5vw,2.5rem)',background:'linear-gradient(135deg,#FFE082,#FFB800)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>Crafted with Devotion</h2>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:'1.5rem'}}>
            {[
              {icon:'🔱',t:'Puja Consecrated',d:'Every product is ritually blessed with Vedic mantras before dispatch for maximum spiritual benefit.'},
              {icon:'🌿',t:'Ethically Sourced',d:'Products sourced responsibly from traditional artisans and certified stone merchants across India.'},
              {icon:'📜',t:'Vedic Authenticity',d:'Backed by pandits and jyotishis who verify the quality and spiritual standards of each product.'},
              {icon:'💛',t:'Devotion First',d:'We are devotees first, retailers second. Your spiritual growth is our sacred mission.'},
            ].map((f,i)=>(
              <div key={f.t} className="fi" style={{background:'rgba(255,184,0,.04)',border:'1px solid rgba(255,184,0,.12)',borderRadius:8,padding:'2rem 1.5rem',textAlign:'center',animationDelay:`${i*.08}s`}}>
                <div style={{fontSize:'2rem',marginBottom:'1rem'}}>{f.icon}</div>
                <div style={{fontFamily:"'Cinzel',serif",fontSize:'.82rem',fontWeight:600,letterSpacing:'.08em',color:'#FFE082',marginBottom:'.7rem'}}>{f.t}</div>
                <div style={{fontSize:'.82rem',color:'rgba(255,224,130,.5)',fontStyle:'italic',lineHeight:1.65,fontFamily:"'Crimson Pro',serif"}}>{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ BLOG ══ */}
      <section style={{padding:'5rem 1.5rem'}}>
        <div style={{maxWidth:1100,margin:'0 auto'}}>
          <div className="fi" style={{textAlign:'center',marginBottom:'3rem'}}>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:'.65rem',letterSpacing:'.35em',textTransform:'uppercase',color:'#FF6B00',marginBottom:'.6rem'}}>✦ Sacred Knowledge ✦</div>
            <h2 style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'clamp(1.6rem,3.5vw,2.5rem)',background:'linear-gradient(135deg,#FFE082,#FFB800)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',marginBottom:'.8rem'}}>Wisdom Articles</h2>
            <div style={{width:60,height:2,background:'linear-gradient(90deg,transparent,#FF6B00,transparent)',margin:'0 auto'}} />
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:'1.5rem'}}>
            {BLOG_POSTS.map((b,i)=>(
              <div key={b.title} className="fi" style={{background:'linear-gradient(135deg,rgba(255,107,0,.06),rgba(36,0,70,.2))',border:'1px solid rgba(255,184,0,.15)',borderRadius:10,overflow:'hidden',animationDelay:`${i*.1}s`}}>
                <div style={{padding:'2rem 1.8rem 1rem',textAlign:'center',background:'rgba(255,184,0,.06)',fontSize:'2.5rem'}}>{b.emoji}</div>
                <div style={{padding:'1.2rem 1.5rem 1.8rem'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'.7rem'}}>
                    <span style={{fontFamily:"'Cinzel',serif",fontSize:'.6rem',letterSpacing:'.15em',textTransform:'uppercase',color:'#FF6B00',border:'1px solid rgba(255,107,0,.3)',borderRadius:20,padding:'.2rem .7rem'}}>{b.category}</span>
                    <span style={{fontSize:'.7rem',color:'rgba(255,224,130,.35)',fontFamily:"'Crimson Pro',serif"}}>{b.date}</span>
                  </div>
                  <h3 style={{fontFamily:"'Cinzel',serif",fontSize:'.92rem',fontWeight:700,color:'#FFE082',marginBottom:'.7rem',lineHeight:1.4}}>{b.title}</h3>
                  <p style={{fontSize:'.82rem',color:'rgba(255,224,130,.5)',lineHeight:1.65,fontStyle:'italic',fontFamily:"'Crimson Pro',serif"}}>{b.excerpt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ NEWSLETTER ══ */}
      <section style={{padding:'5rem 1.5rem',background:'linear-gradient(135deg,rgba(255,107,0,.12),rgba(36,0,70,.4),rgba(255,107,0,.1))',textAlign:'center',borderTop:'1px solid rgba(255,184,0,.15)'}}>
        <div style={{maxWidth:560,margin:'0 auto'}}>
          <div className="fi" style={{fontSize:'2.5rem',marginBottom:'1rem'}}>🪷</div>
          <h2 className="fi" style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'clamp(1.4rem,3vw,2rem)',background:'linear-gradient(135deg,#FFE082,#FFB800)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',marginBottom:'.7rem'}}>Join Our Sacred Circle</h2>
          <p className="fi" style={{fontFamily:"'Crimson Pro',serif",fontSize:'1rem',color:'rgba(255,224,130,.55)',fontStyle:'italic',marginBottom:'2rem',lineHeight:1.7}}>
            Receive sacred knowledge, festival updates, exclusive offers, and spiritual guidance delivered to your heart.
          </p>
          <form className="fi" onSubmit={e=>e.preventDefault()} style={{display:'flex',gap:'.7rem',flexWrap:'wrap',justifyContent:'center'}}>
            <input type="email" placeholder="your@email.com" style={{flex:1,minWidth:220,padding:'.85rem 1.2rem',background:'rgba(255,255,255,.08)',border:'1px solid rgba(255,184,0,.3)',borderRadius:4,color:'#FFE082',fontFamily:"'Crimson Pro',serif",fontSize:'1rem',outline:'none'}} />
            <button type="submit" style={{padding:'.85rem 1.8rem',fontFamily:"'Cinzel',serif",fontSize:'.75rem',fontWeight:700,letterSpacing:'.15em',textTransform:'uppercase',background:'linear-gradient(135deg,#FF6B00,#FFB800)',color:'#0D0500',border:'none',borderRadius:4,cursor:'pointer'}}>
              🙏 Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
